import {
  type BulkEvaluationInput,
  bulkEvaluate as bulkEvalFlags,
  evaluate as evalFlag,
} from './evaluate';
import { internalReportValue } from './lib/report-value';
import type {
  BulkEvaluateInput,
  BundledDefinitions,
  ControllerInterface,
  Datafile,
  EvaluationResult,
  Metrics,
  Packed,
} from './types';
import { ErrorCode, ResolutionReason } from './types';
import type { TrackEvaluationOptions } from './utils/usage/flags-evaluation';

export type ControllerInstance = {
  controller: ControllerInterface;
  initialized: boolean;
  initPromise: Promise<void> | null;
};

export const controllerInstanceMap = new Map<number, ControllerInstance>();

type EvaluationTrackingController = ControllerInterface & {
  trackEvaluation?: (options: TrackEvaluationOptions) => void;
};

function getInstance(id: number): ControllerInstance {
  const instance = controllerInstanceMap.get(id);
  if (!instance) {
    throw new Error(
      `@vercel/flags-core: Client instance ${id} not found. It may have been shut down.`,
    );
  }
  return instance;
}

export function initialize(id: number): Promise<void> {
  return getInstance(id).controller.initialize();
}

export function shutdown(id: number): void | Promise<void> {
  return getInstance(id).controller.shutdown();
}

export function getDatafile(id: number) {
  return getInstance(id).controller.getDatafile();
}

export function getFallbackDatafile(id: number): Promise<BundledDefinitions> {
  const ds = getInstance(id).controller;
  if (ds.getFallbackDatafile) return ds.getFallbackDatafile();
  throw new Error('flags: This data source does not support fallbacks');
}

function trackEvaluation(
  controller: EvaluationTrackingController,
  options: TrackEvaluationOptions,
): void {
  controller.trackEvaluation?.(options);
}

export async function evaluate<T, E = Record<string, unknown>>(
  id: number,
  flagKey: string,
  defaultValue?: T,
  entities?: E,
): Promise<EvaluationResult<T>> {
  const controller = getInstance(id).controller as EvaluationTrackingController;

  let datafile: Datafile;
  try {
    datafile = await controller.read();
  } catch (error) {
    // All data sources failed. Fall back to defaultValue if provided.
    if (defaultValue !== undefined) {
      const result: EvaluationResult<T> = {
        value: defaultValue,
        variantId: null,
        reason: ResolutionReason.ERROR,
        errorMessage:
          error instanceof Error ? error.message : 'Failed to read datafile',
      };

      trackEvaluation(controller, {
        flagKey,
        variant: null,
        reason: result.reason,
      });

      return result;
    }
    throw error;
  }

  const flagDefinition = datafile.definitions[flagKey] as Packed.FlagDefinition;

  if (flagDefinition === undefined) {
    if (datafile.projectId) {
      internalReportValue(flagKey, defaultValue, {
        originProjectId: datafile.projectId,
        originProvider: 'vercel',
        reason: ResolutionReason.ERROR,
      });
    }

    const result: EvaluationResult<T> = {
      value: defaultValue,
      variantId: null,
      reason: ResolutionReason.ERROR,
      errorCode: ErrorCode.FLAG_NOT_FOUND,
      errorMessage: `@vercel/flags-core: Definition not found for flag "${flagKey}"`,
      metrics: {
        evaluationMs: 0,
        readMs: datafile.metrics.readMs,
        source: datafile.metrics.source,
        cacheStatus: datafile.metrics.cacheStatus,
        connectionState: datafile.metrics.connectionState,
        mode: datafile.metrics.mode,
      },
    };
    trackEvaluation(controller, {
      flagKey,
      variant: null,
      reason: result.reason,
    });
    return result;
  }

  const evalStartTime = Date.now();
  const result = evalFlag<T>({
    defaultValue,
    definition: flagDefinition,
    environment: datafile.environment,
    entities: entities ?? {},
    segments: datafile.segments,
  });
  const evaluationDurationMs = Date.now() - evalStartTime;

  if (datafile.projectId) {
    internalReportValue(flagKey, result.value, {
      originProjectId: datafile.projectId,
      originProvider: 'vercel',
      reason: result.reason,
      outcomeType:
        result.reason !== ResolutionReason.ERROR
          ? result.outcomeType
          : undefined,
    });
  }

  trackEvaluation(controller, {
    flagKey,
    variant: result.variantId,
    reason: result.reason,
  });

  return Object.assign(result, {
    metrics: {
      evaluationMs: evaluationDurationMs,
      readMs: datafile.metrics.readMs,
      source: datafile.metrics.source,
      cacheStatus: datafile.metrics.cacheStatus,
      connectionState: datafile.metrics.connectionState,
      mode: datafile.metrics.mode,
    },
  });
}

export async function bulkEvaluate<T, E = Record<string, unknown>>(
  id: number,
  flags: BulkEvaluateInput<T>[],
  entities?: E,
): Promise<Record<string, EvaluationResult<T>>> {
  const controller = getInstance(id).controller as EvaluationTrackingController;

  let datafile: Datafile;
  try {
    datafile = await controller.read();
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to read datafile';

    const results: Record<string, EvaluationResult<T>> = {};
    for (const flag of flags) {
      results[flag.key] = {
        value: flag.defaultValue,
        reason: ResolutionReason.ERROR,
        errorMessage,
        variantId: null,
      };
      trackEvaluation(controller, {
        flagKey: flag.key,
        variant: null,
        reason: ResolutionReason.ERROR,
      });
    }
    return results;
  }

  const baseMetrics: Metrics = {
    readMs: datafile.metrics.readMs,
    source: datafile.metrics.source,
    cacheStatus: datafile.metrics.cacheStatus,
    connectionState: datafile.metrics.connectionState,
    mode: datafile.metrics.mode,
  };

  const projectId = datafile.projectId;
  const results: Record<string, EvaluationResult<T>> = {};
  const toEvaluate: Record<string, BulkEvaluationInput<T>> = {};

  for (const flag of flags) {
    const { key, defaultValue } = flag;
    const flagDefinition = datafile.definitions[key] as Packed.FlagDefinition;

    if (flagDefinition === undefined) {
      if (projectId) {
        internalReportValue(key, defaultValue, {
          originProjectId: projectId,
          originProvider: 'vercel',
          reason: ResolutionReason.ERROR,
        });
      }
      results[key] = {
        value: defaultValue,
        reason: ResolutionReason.ERROR,
        errorCode: ErrorCode.FLAG_NOT_FOUND,
        errorMessage: `@vercel/flags-core: Definition not found for flag "${key}"`,
        metrics: { evaluationMs: 0, ...baseMetrics },
        variantId: null,
      };
      trackEvaluation(controller, {
        flagKey: key,
        variant: null,
        reason: ResolutionReason.ERROR,
      });
      continue;
    }

    toEvaluate[key] = { definition: flagDefinition, defaultValue };
  }

  const evalStartTime = Date.now();
  const evaluated = bulkEvalFlags<T>(toEvaluate, {
    entities: (entities ?? {}) as Record<string, unknown>,
    environment: datafile.environment,
    segments: datafile.segments,
  });
  const evaluationDurationMs = Date.now() - evalStartTime;

  for (const key in toEvaluate) {
    const result = evaluated[key]!;
    if (projectId) {
      internalReportValue(key, result.value, {
        originProjectId: projectId,
        originProvider: 'vercel',
        reason: result.reason,
        outcomeType:
          result.reason !== ResolutionReason.ERROR
            ? result.outcomeType
            : undefined,
      });
    }
    trackEvaluation(controller, {
      flagKey: key,
      variant: result.variantId,
      reason: result.reason,
    });
    results[key] = Object.assign(result, {
      metrics: { evaluationMs: evaluationDurationMs, ...baseMetrics },
    });
  }

  return results;
}
